// DRI-253: one group per person on the Facebook Connections page: someone whose own Facebook login connects
// businesses (the fallback when no system user of ours reaches both a business's ad account and page), with
// their token health and a Clear errors action.
import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import RefreshIcon from '@material-ui/icons/Refresh'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import { businessManagerService } from 'app/service/business-manager'
import {
  AuthorizerConnectionsByUser,
  AuthorizerTokenStatus,
  BusinessError,
} from '@marketing-milk/interfaces'

import { CopyableId, FlagLine } from './SystemUserRows'
import { businessMatches, normalizeSearch, textMatches } from './search'
import {
  BUSINESS_ERROR_LABELS,
  hasCritical,
  hasWarning,
  isFiltering,
  IssueFilter,
  matchesIssueFilter,
  severityStyle,
} from './errorLabels'

const BUSINESSES_PER_PAGE = 25

export interface BusinessRow {
  business_id: number
  business_name: string
  // the Ad Account and Page ids this person connects for the business
  ad_account_id?: string
  page_id?: string
  has_ad: boolean
  has_page: boolean
  errors: BusinessError[]
}

// errors that only matter to the authorizer of the connection type they apply to
const AD_ONLY_ERRORS: BusinessError[] = [
  BusinessError.ad_account_access_error,
  BusinessError.offline_upload_error,
]
const PAGE_ONLY_ERRORS: BusinessError[] = [
  BusinessError.page_access_error,
  BusinessError.lead_access_error,
]

function filterRelevantErrors(
  errors: BusinessError[],
  has_ad: boolean,
  has_page: boolean
): BusinessError[] {
  return errors.filter(e => {
    if (AD_ONLY_ERRORS.includes(e)) return has_ad
    if (PAGE_ONLY_ERRORS.includes(e)) return has_page
    return true
  })
}

export function toPersonBusinessRows(user: AuthorizerConnectionsByUser): BusinessRow[] {
  const map = new Map<number, BusinessRow>()
  for (const conn of user.connections) {
    const existing = map.get(conn.business_id)
    if (existing) {
      existing.has_ad = existing.has_ad || conn.connection_type === 'fb_ad'
      existing.has_page = existing.has_page || conn.connection_type === 'fb_page'
      if (conn.connection_type === 'fb_ad') existing.ad_account_id = conn.asset_id
      if (conn.connection_type === 'fb_page') existing.page_id = conn.asset_id
      existing.errors = Array.from(new Set([...existing.errors, ...(conn.errors ?? [])]))
    } else {
      map.set(conn.business_id, {
        business_id: conn.business_id,
        business_name: conn.business_name,
        has_ad: conn.connection_type === 'fb_ad',
        has_page: conn.connection_type === 'fb_page',
        ad_account_id: conn.connection_type === 'fb_ad' ? conn.asset_id : undefined,
        page_id: conn.connection_type === 'fb_page' ? conn.asset_id : undefined,
        errors: conn.errors ? [...conn.errors] : [],
      })
    }
  }
  return Array.from(map.values()).map(row => ({
    ...row,
    errors: filterRelevantErrors(row.errors, row.has_ad, row.has_page),
  }))
}

function AssetLine({
  label,
  id,
  background,
  color,
}: {
  label: string
  id?: string
  background: string
  color: string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Chip
        label={label}
        size="small"
        style={{ height: 18, fontSize: 11, background, color, fontWeight: 600 }}
      />
      {id && (
        <>
          <Typography variant="caption" color="textSecondary" style={{ fontFamily: 'monospace' }}>
            {id}
          </Typography>
          <CopyableId value={id} label={`${label} ID`} />
        </>
      )}
    </div>
  )
}

export function PersonRow({
  authorizer,
  searchQuery,
  currentUserId,
  issueFilter,
  tokenStatus,
  tokenStatusLoading,
  onErrorsCleared,
}: {
  authorizer: AuthorizerConnectionsByUser
  searchQuery: string
  currentUserId: number
  issueFilter: IssueFilter
  tokenStatus: AuthorizerTokenStatus | undefined
  tokenStatusLoading: boolean
  onErrorsCleared: (userID: number) => void
}) {
  const [open, setOpen] = useState(false)
  const [bizPage, setBizPage] = useState(1)

  // Reset biz page when search changes
  useEffect(() => {
    setBizPage(1)
  }, [searchQuery, issueFilter])

  const allRows = useMemo(() => toPersonBusinessRows(authorizer), [authorizer])

  const filteredRows = useMemo(() => {
    const q = normalizeSearch(searchQuery)
    let rows = allRows
    if (isFiltering(issueFilter)) rows = rows.filter(r => matchesIssueFilter(r.errors, issueFilter))
    if (!q) return rows
    // a query matching the person themselves qualifies all of their businesses
    if (textMatches(authorizer.user_name, q) || textMatches(authorizer.user_email, q)) return rows
    return rows.filter(r => businessMatches(r, [r.ad_account_id, r.page_id], q))
  }, [allRows, searchQuery, issueFilter])

  const totalPages = Math.ceil(filteredRows.length / BUSINESSES_PER_PAGE)
  const pageRows = filteredRows.slice(
    (bizPage - 1) * BUSINESSES_PER_PAGE,
    bizPage * BUSINESSES_PER_PAGE
  )
  const errorCount = filteredRows.filter(r => hasCritical(r.errors)).length
  const warningCount = filteredRows.filter(r => hasWarning(r.errors)).length
  const [clearingErrors, setClearingErrors] = useState(false)

  async function handleClearErrors(e: React.MouseEvent) {
    e.stopPropagation()
    setClearingErrors(true)
    try {
      await businessManagerService.clearErrorsForAuthorizer(authorizer.user_id)
      onErrorsCleared(authorizer.user_id)
    } finally {
      setClearingErrors(false)
    }
  }

  // Auto-open when search narrows results
  useEffect(() => {
    if (searchQuery.trim() && filteredRows.length > 0) setOpen(true)
  }, [searchQuery, filteredRows.length])

  if (filteredRows.length === 0 && searchQuery.trim()) return null

  return (
    <div
      style={{ marginBottom: 8, border: '1px solid #e0e0e0', borderRadius: 4, overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          cursor: 'pointer',
          background: open ? '#fafafa' : '#fff',
          userSelect: 'none',
        }}
        onClick={() => setOpen(v => !v)}
      >
        <IconButton
          size="small"
          style={{ padding: 2 }}
          onClick={e => {
            e.stopPropagation()
            setOpen(v => !v)
          }}
        >
          {open ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon fontSize="small" />
          )}
        </IconButton>
        {authorizer.user_profile_pic ? (
          <img
            src={authorizer.user_profile_pic}
            alt={authorizer.user_name}
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              flexShrink: 0,
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: '#EEF4FF',
              color: '#1877F2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {authorizer.user_name?.[0]?.toUpperCase() ?? '?'}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, lineHeight: 1.2 }}>
            {authorizer.user_name || '—'}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="textSecondary">
              {authorizer.user_email}
            </Typography>
            {authorizer.user_id === currentUserId && (
              <Link
                href="/admin/preferences/account"
                variant="caption"
                style={{ fontWeight: 600 }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                Refresh your token →
              </Link>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {tokenStatusLoading ? (
            <Chip
              icon={<CircularProgress size={10} style={{ marginLeft: 4 }} />}
              label="Checking token..."
              size="small"
              style={{ background: '#f5f5f5', color: '#9e9e9e' }}
            />
          ) : tokenStatus !== undefined ? (
            <Tooltip title={tokenStatus.is_valid ? 'Token valid' : 'Token invalid or expired'}>
              <Chip
                icon={
                  tokenStatus.is_valid ? (
                    <CheckCircleIcon style={{ fontSize: 13, color: '#16a34a' }} />
                  ) : (
                    <ErrorIcon style={{ fontSize: 13 }} />
                  )
                }
                label={tokenStatus.is_valid ? 'Token valid' : 'Token invalid'}
                size="small"
                style={
                  tokenStatus.is_valid
                    ? { background: '#DCFCE7', color: '#15803d' }
                    : { background: '#FEE2E2', color: '#B91C1C' }
                }
              />
            </Tooltip>
          ) : null}
          <Chip
            label={`${allRows.length} business${allRows.length !== 1 ? 'es' : ''}`}
            size="small"
          />
          {errorCount > 0 && (
            <Chip
              icon={<WarningIcon style={{ fontSize: 13 }} />}
              label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
              size="small"
              style={{
                background: severityStyle.critical.chipBackground,
                color: severityStyle.critical.chipText,
              }}
            />
          )}
          {warningCount > 0 && (
            <Chip
              icon={<WarningIcon style={{ fontSize: 13 }} />}
              label={`${warningCount} warning${warningCount !== 1 ? 's' : ''}`}
              size="small"
              style={{
                background: severityStyle.warning.chipBackground,
                color: severityStyle.warning.chipText,
              }}
            />
          )}
          {errorCount + warningCount > 0 && (
            <>
              <Tooltip title="Attempt to clear all errors for this user">
                <span onClick={e => e.stopPropagation()}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      clearingErrors ? (
                        <CircularProgress size={12} />
                      ) : (
                        <RefreshIcon style={{ fontSize: 14 }} />
                      )
                    }
                    disabled={clearingErrors}
                    onClick={handleClearErrors}
                    style={{ fontSize: 11, padding: '2px 8px', height: 24 }}
                  >
                    Clear errors
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <Collapse in={open} unmountOnExit>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: '#f5f5f5' }}>
                <TableCell>Business</TableCell>
                <TableCell>Connections</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map(row => (
                <TableRow key={row.business_id}>
                  <TableCell>
                    <Link
                      href={`/admin/business/beta/accounts/edit/${row.business_id}`}
                      variant="body2"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      {row.business_name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {row.has_ad && (
                        <AssetLine
                          label="Ad Account"
                          id={row.ad_account_id}
                          background="#EEF4FF"
                          color="#1877F2"
                        />
                      )}
                      {row.has_page && (
                        <AssetLine
                          label="Page"
                          id={row.page_id}
                          background="#F0F9FF"
                          color="#0284C7"
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.errors.length === 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircleIcon style={{ fontSize: 15, color: '#4caf50' }} />
                        <Typography variant="caption" style={{ color: '#757575' }}>
                          Connected
                        </Typography>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {row.errors.map(err => (
                          <FlagLine key={err} error={err} label={BUSINESS_ERROR_LABELS[err]} />
                        ))}
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '6px 12px',
              borderTop: '1px solid #e0e0e0',
            }}
            onClick={e => e.stopPropagation()}
          >
            <Typography variant="caption" color="textSecondary">
              {(bizPage - 1) * BUSINESSES_PER_PAGE + 1}–
              {Math.min(bizPage * BUSINESSES_PER_PAGE, filteredRows.length)} of{' '}
              {filteredRows.length}
            </Typography>
            <Tooltip title="Previous">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setBizPage(p => p - 1)}
                  disabled={bizPage <= 1}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setBizPage(p => p + 1)}
                  disabled={bizPage >= totalPages}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      </Collapse>
    </div>
  )
}
